/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Grid, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import type { FC } from 'react';
import React from 'react';
import type { SortableEvent } from 'react-sortablejs';
import { ReactSortable } from 'react-sortablejs';
import { CustomIconButton } from '@opengeoweb/shared';
import { Copy, Delete, Visibility, VisibilityOff } from '@opengeoweb/theme';
import TimeSeriesManagerRowAccordion from './TimeSeriesManagerRowAccordion';
import type { Parameter, ParameterListStyles, Plot } from '../TimeSeries/types';
import { ParameterInfoButtonConnect } from './ParameterInfo/ParameterInfoButtonConnect';
import { RowLayout } from './RowLayout';
import ParameterColorSelect from './TimeSeriesManagerParameterComponents/ParameterColorSelect';
import ParameterDimensionSelect from './TimeSeriesManagerParameterComponents/ParameterDimensionSelect';
import ParameterNameSelect from './TimeSeriesManagerParameterComponents/ParameterNameSelect';
import ParameterOpacitySelect from './TimeSeriesManagerParameterComponents/ParameterOpacitySelect';
import ParameterTypeSelect from './TimeSeriesManagerParameterComponents/ParameterTypeSelect';
import type { MoveParameterPayload } from '../../store/types';

export const ParameterList: FC<{
  panelId: string;
  plot: Plot;
  plotIsEnabled: boolean;
  deleteParameter: (id: string) => void;
  toggleParameter: (id: string) => void;
  patchParameter: (parameter: Partial<Parameter>) => void;
  addParameter: (parameter: Parameter) => void;
  moveParameter: (event: MoveParameterPayload) => void;
  t: TFunction;
}> = ({
  panelId,
  plot,
  plotIsEnabled,
  deleteParameter,
  toggleParameter,
  patchParameter,
  addParameter,
  moveParameter,
  t,
}) => {
  const onSortEnd = React.useCallback(
    (sort: SortableEvent): void => {
      moveParameter({
        oldIndex: sort.oldIndex,
        newIndex: sort.newIndex,
        plotId: sort.from.id,
        toPlotId: sort.to.id,
      } as MoveParameterPayload);
    },
    [moveParameter],
  );

  return (
    <ReactSortable
      animation={200}
      direction="vertical"
      group={{ name: 'parameter', pull: true, put: ['parameter'] }}
      id={plot.plotId}
      list={
        plot.parameters
          ? plot.parameters?.map((param) => ({ id: param.plotId }))
          : []
      }
      onEnd={onSortEnd}
      setList={(): void => {}}
      swapThreshold={0.3} // Nested sortable gets jumpy without this. Cant be lower because e2e will fail.
    >
      {plot.parameters?.map((parameter) => {
        const parameterIsEnabled = parameter.enabled !== false;
        const rowIsEnabled = plotIsEnabled && parameterIsEnabled;
        const styles = getStyles(rowIsEnabled);
        return (
          <Box key={`${parameter.id}`} sx={styles.plot}>
            <Box className="medium-timeseriesmanager">
              <TimeSeriesManagerRowAccordion
                addParameter={addParameter}
                deleteParameter={deleteParameter}
                panelId={panelId}
                parameter={parameter}
                patchParameter={patchParameter}
                plotIsEnabled={plotIsEnabled}
                rowIsEnabled={rowIsEnabled}
                styles={styles}
                t={t}
                toggleParameter={toggleParameter}
              />
            </Box>
            <RowLayout
              actions={
                <Grid
                  container
                  sx={{
                    justifyContent: 'space-between',
                  }}
                >
                  <ParameterInfoButtonConnect
                    panelId={panelId}
                    parameter={parameter}
                  />{' '}
                  <CustomIconButton
                    data-testid="duplicateButton"
                    onClick={(): void => {
                      addParameter(parameter);
                    }}
                    shouldShowAsDisabled={!plotIsEnabled}
                    tooltipTitle={`${t('timeseries-duplicate')} ${parameter.propertyName}`}
                  >
                    <Copy />
                  </CustomIconButton>
                  <CustomIconButton
                    data-testid="deleteButton"
                    onClick={(): void => {
                      deleteParameter(parameter.id!);
                    }}
                    shouldShowAsDisabled={!plotIsEnabled}
                    tooltipTitle={`${t('timeseries-remove')} ${parameter.propertyName}`}
                  >
                    <Delete />
                  </CustomIconButton>
                </Grid>
              }
              className="plotRow"
              collection={
                <Typography sx={{ ...styles.rowText }}>
                  {parameter.collectionId}
                </Typography>
              }
              dimension={
                <ParameterDimensionSelect
                  parameter={parameter}
                  patchParameter={patchParameter}
                  rowIsEnabled={rowIsEnabled}
                  t={t}
                />
              }
              parameter={
                <ParameterNameSelect
                  parameter={parameter}
                  patchParameter={patchParameter}
                  rowIsEnabled={rowIsEnabled}
                  t={t}
                />
              }
              plot={
                <CustomIconButton
                  data-testid="enableButton"
                  onClick={(): void => {
                    toggleParameter(parameter.id!);
                  }}
                  shouldShowAsDisabled={!rowIsEnabled}
                  sx={{ marginLeft: 3 }}
                  tooltipTitle={`${
                    parameterIsEnabled
                      ? t('timeseries-hide')
                      : t('timeseries-show')
                  } ${parameter.propertyName}`}
                >
                  {rowIsEnabled ? <Visibility /> : <VisibilityOff />}
                </CustomIconButton>
              }
              style={
                <Grid container>
                  <Grid size="grow">
                    <ParameterTypeSelect
                      parameter={parameter}
                      patchParameter={patchParameter}
                      rowIsEnabled={rowIsEnabled}
                      t={t}
                    />
                  </Grid>
                  <Grid>
                    <ParameterColorSelect
                      parameter={parameter}
                      patchParameter={patchParameter}
                      rowIsEnabled={rowIsEnabled}
                      t={t}
                    />
                  </Grid>
                  <Grid>
                    <ParameterOpacitySelect
                      parameter={parameter}
                      patchParameter={patchParameter}
                      rowIsEnabled={rowIsEnabled}
                      t={t}
                    />
                  </Grid>
                </Grid>
              }
              sx={styles.row}
            />
          </Box>
        );
      })}
    </ReactSortable>
  );
};

const getStyles = (isEnabled: boolean): ParameterListStyles => ({
  plot: {
    marginBottom: '4px',
  },
  row: {
    backgroundColor: isEnabled
      ? 'geowebColors.layerManager.tableRowDefaultCardContainer.fill'
      : 'geowebColors.layerManager.tableRowDisabledCardContainer.fill',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: 'geowebColors.cards.cardContainerBorder',
    borderRadius: 1,
    marginBottom: 0.25,
    height: 33,
    width: '100%',
  },
  rowText: {
    fontSize: 12,
    fontWeight: 500,
    color: isEnabled
      ? 'geowebColors.layerManager.tableRowDefaultText.color'
      : 'geowebColors.layerManager.tableRowDisabledText.color',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
});
